/**
 * Can this person install the app from here, and how?
 *
 * The honest answer differs by platform, and pretending otherwise produces the
 * worst version of this feature — a button that does nothing on half the
 * phones in the field.
 *
 *   • Chrome / Edge (Android and desktop) fire `beforeinstallprompt`. Captured,
 *     that is a real one-tap install: our button opens the browser's own
 *     dialog. Nothing else can trigger it, and it can only be used once.
 *   • iOS gives no such API at all. Safari's "Lisa avakuvale" is a menu the
 *     user must walk themselves, so the most a page can do is point at the
 *     right button. This is Apple's decision, not a gap in our code.
 *   • Inside Chrome or Firefox ON iOS there is not even that: those browsers
 *     have no Add to Home Screen item, so the only useful instruction is
 *     "open this in Safari".
 *
 * The detection below is pure — it takes the environment rather than reading
 * globals — so each of those cases is pinned by a test instead of by whichever
 * phone happened to be on the desk.
 */

export type InstallMode =
  /** Already installed and running from the home screen — say nothing. */
  | 'installed'
  /** A real prompt is available: one tap. */
  | 'prompt'
  /** iOS Safari: show the Share → Add to Home Screen steps. */
  | 'ios-safari'
  /** iOS in a non-Safari browser: tell them to open it in Safari first. */
  | 'ios-other'
  /** Desktop Firefox, Samsung Internet, anything else — no install path. */
  | 'unsupported';

export interface InstallEnv {
  userAgent: string;
  /** True when the page is running as an installed app. */
  standalone: boolean;
  /** True once a `beforeinstallprompt` event has been captured. */
  hasPrompt: boolean;
  /** iPadOS 13+ reports a Mac UA; touch points are what give it away. */
  maxTouchPoints?: number;
}

/** iOS, including an iPad pretending to be a Mac. */
function isIos(env: InstallEnv): boolean {
  if (/iPad|iPhone|iPod/.test(env.userAgent)) return true;
  return env.userAgent.includes('Macintosh') && (env.maxTouchPoints ?? 0) > 1;
}

/**
 * Every browser on iOS is WebKit underneath, but only Safari has the Add to
 * Home Screen item — so the in-app browsers people arrive from (Chrome,
 * Firefox, Edge) are a dead end until they reopen the link in Safari.
 */
function isIosSafari(env: InstallEnv): boolean {
  return isIos(env) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(env.userAgent);
}

export function detectInstallMode(env: InstallEnv): InstallMode {
  if (env.standalone) return 'installed';
  if (env.hasPrompt) return 'prompt';
  if (isIos(env)) return isIosSafari(env) ? 'ios-safari' : 'ios-other';
  return 'unsupported';
}

/** Nothing useful to say — render nothing rather than an inert card. */
export function shouldOfferInstall(mode: InstallMode): boolean {
  return mode === 'prompt' || mode === 'ios-safari' || mode === 'ios-other';
}

/** localStorage key holding the ISO instant the user last dismissed the card. */
export const DISMISS_KEY = 'rm24.installPrompt.dismissedAt';

/**
 * Ask again after a while, but not soon.
 *
 * Someone who closes this is saying "not now", not "never" — a colleague may
 * still be waiting to be told the app exists. Coming back the next day would
 * be nagging; a month later is a reminder.
 */
export const REASK_AFTER_DAYS = 30;

export function isDismissed(dismissedAt: string | null, nowIso: string): boolean {
  if (!dismissedAt) return false;
  const then = Date.parse(dismissedAt);
  const now = Date.parse(nowIso);
  if (Number.isNaN(then) || Number.isNaN(now)) return false;
  return now - then < REASK_AFTER_DAYS * 86_400_000;
}
